import { BaseCurrency, getFormatter, isDefined, OrderFormData, TradeType, useDictionary } from '@eih-netbroker/common';
import { useEffect, useEffectEvent } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { FeedbackBox } from '../../../../../shared/components/feedback-box/FeedbackBox';
import { NbIndefiniteLoaderValueWrapper } from '../../../../../shared/components/loader-value/NbIndefiniteLoaderValueWrapper';
import { MoneyMaskHOC } from '../../../../../shared/components/money-mask/MoneyMask';
import { useAccounts } from '../../../../../shared/services/useAccounts';
import { cssWhen } from '../../../../../shared/utils/themeUtils';
import { useLoadBalanceForCurrency } from '../../../services/order/useLoadBalanceForCurrency';
import { OrderFormSourceComponent } from '../../../types/orderTypes';
import classes from './OrderTransactionInfo.module.scss';
import { OrderTransactionInfoOrderBuyInfo } from './OrderTransactionInfoBuyInfo';
import { OrderTransactionInfoSellInfo } from './OrderTransactionInfoSellInfo';

export type OrderTransactionInfoProps = {
  className?: string;
  order: OrderFormData;
  sourceComponent?: OrderFormSourceComponent;
};

const unknownValue = '-';

export const OrderTransactionInfo = ({ className, order, sourceComponent }: OrderTransactionInfoProps) => {
  const { selectedAccountOrClient, isMultipleAccountSelected } = useAccounts();
  const { formatDictionaryType } = useDictionary();
  const { translation } = useLocale();
  const { formatCurrency } = getFormatter();
  const { loadBalanceForCurrency, balanceForCurrency } = useLoadBalanceForCurrency();

  const accountCategoryLabel = selectedAccountOrClient.account?.accountCategory
    ? formatDictionaryType(selectedAccountOrClient.account.accountCategory, 'accountCategoryTypes')
    : unknownValue;

  const refreshBalanceForCurrency = useEffectEvent(async (): Promise<void> => {
    if (!isDefined(order.instrument) || isMultipleAccountSelected || !isDefined(selectedAccountOrClient.account)) {
      return;
    }

    loadBalanceForCurrency(
      order.instrument.currency,
      selectedAccountOrClient.account?.accountNumber,
      order.instrument.mic,
    );
  });

  useEffect(() => {
    refreshBalanceForCurrency();
  }, [selectedAccountOrClient]);

  if (isMultipleAccountSelected && isDefined(order.instrument)) {
    return (
      <div className={cssWhen(classes.container, className)}>
        <div className={classes.row}>
          <span
            className={classes.label}
            data-testid="order-transaction-info-account-label"
          >
            {translation.orderModal.transactionInfo.account}
          </span>
          <span
            className={classes.value}
            data-testid="order-transaction-info-account-value"
          >
            {translation.orderModal.transactionInfo.allAccounts}
          </span>
        </div>
        <FeedbackBox
          type="warning"
          message={translation.orderModal.transactionInfo.selectAccountWarning}
          className={classes.warningText}
          isWithoutBorder
          data-testid="order-transaction-info-warning"
        />
      </div>
    );
  }

  return (
    <div className={cssWhen(classes.container, className)}>
      <div className={classes.row}>
        <span
          className={classes.label}
          data-testid="order-transaction-info-account-label"
        >
          {translation.orderModal.transactionInfo.account}
        </span>
        <span
          className={classes.value}
          data-testid="order-transaction-info-account-value"
        >
          {accountCategoryLabel}
        </span>
      </div>
      <div className={classes.divider} />
      <div className={classes.transactionInfoContent}>
        <div className={classes.row}>
          <span
            className={classes.label}
            data-testid="order-transaction-info-investable-label"
          >
            {translation.orderModal.transactionInfo.investable}
          </span>
          {!isDefined(order.instrument) && (
            <span
              className={classes.value}
              data-testid="order-transaction-info-investable-value"
            >
              {unknownValue}
            </span>
          )}
          {isDefined(order.instrument) && (
            <MoneyMaskHOC
              isInverse
              size={100}
              bypass={sourceComponent === OrderFormSourceComponent.Modal}
            >
              <NbIndefiniteLoaderValueWrapper
                loadableValue={balanceForCurrency}
                loadedValueCb={loadedBalanceForCurrency => (
                  <span
                    className={classes.value}
                    data-testid="order-transaction-info-investable-value"
                  >
                    {isDefined(order.instrument)
                      ? formatCurrency(
                          loadedBalanceForCurrency.value,
                          order.instrument.currency ?? BaseCurrency,
                          order.instrument?.currency === BaseCurrency,
                        )
                      : unknownValue}
                  </span>
                )}
              />
            </MoneyMaskHOC>
          )}
        </div>
        {order.tradeType === TradeType.Buy ? (
          <OrderTransactionInfoOrderBuyInfo
            order={order}
            sourceComponent={sourceComponent}
          />
        ) : (
          <OrderTransactionInfoSellInfo
            order={order}
            sourceComponent={sourceComponent}
          />
        )}
      </div>
    </div>
  );
};
